import type { TenantRole } from "@deca/shared-types";

/**
 * Permisos del panel por rol de tenant.
 *
 * El backend ya impone las reglas reales (`assertRole` en cada ruta de
 * escritura, `CONDUCTOR` excluido incluso de la lectura de auditoría — ver
 * `apps/api/src/routes/panel.ts`); esto es la traducción de esas mismas
 * reglas a la interfaz, para que el menú y las rutas no muestren opciones
 * que el servidor va a rechazar de todas formas.
 *
 * `isSuperAdmin` se trata aparte: un super-admin ve siempre todo, igual que
 * en el servidor (`assertRole` deja pasar a `isSuperAdmin` sin mirar el rol).
 */

export type NavSection =
  | "dashboard"
  | "deca"
  | "catalogs"
  | "reports"
  | "audit"
  | "templates"
  | "apiKeys"
  | "users"
  | "tenants"
  | "branding"
  | "billing"
  | "financial"
  | "backup"
  | "storage"
  | "settings";

const ROLE_SECTIONS: Record<TenantRole, NavSection[]> = {
  ADMIN: ["dashboard", "deca", "catalogs", "reports", "audit", "templates", "apiKeys", "users", "financial", "settings"],
  GESTOR: ["dashboard", "deca", "catalogs", "reports", "audit", "templates", "settings"],
  // LECTOR solo lee: ve catálogos y auditoría (el backend deja pasar sus GET),
  // pero no plantillas/claves de API/usuarios, que son de configuración.
  LECTOR: ["dashboard", "deca", "catalogs", "reports", "audit", "settings"],
  // CONDUCTOR no tiene panel: su interfaz es la app móvil (CLAUDE.md Fase 5).
  CONDUCTOR: ["settings"],
};

// "tenants" (gestión de corporaciones/empresas), "branding" (logo de la
// app) y "billing" (planes de facturación/suscripciones) son exclusivas de
// super-admin: ni siquiera un ADMIN de tenant las ve, porque son ajustes de
// la plataforma entera, no de su propio tenant.
// "financial" es la pantalla de finanzas del tenant (historial de facturas,
// estado de suscripción): visible para ADMIN de tenant, pero no es una
// sección de super-admin pura.
// "backup" es exclusiva de super-admin: acceso a volcados de BD y .env.
const ALL_SECTIONS: NavSection[] = [
  "dashboard",
  "deca",
  "catalogs",
  "reports",
  "audit",
  "templates",
  "apiKeys",
  "users",
  "tenants",
  "branding",
  "billing",
  "financial",
  "backup",
  "storage",
  "settings",
];

export function sectionsFor(role: string | null | undefined, isSuperAdmin: boolean): NavSection[] {
  if (isSuperAdmin) return ALL_SECTIONS;
  if (!role) return ["settings"];
  return ROLE_SECTIONS[role as TenantRole] ?? ["settings"];
}

export function canWrite(role: string | null | undefined, isSuperAdmin: boolean): boolean {
  if (isSuperAdmin) return true;
  return role === "ADMIN" || role === "GESTOR";
}

export function isConductor(role: string | null | undefined, isSuperAdmin: boolean): boolean {
  return !isSuperAdmin && role === "CONDUCTOR";
}

/** A qué sección pertenece cada ruta, para el guardia de navegación. */
export function sectionForPath(path: string): NavSection {
  if (path === "/" || path.startsWith("/dashboard")) return "dashboard";
  if (path.startsWith("/deca")) return "deca";
  if (path.startsWith("/contrapartes") || path.startsWith("/vehiculos")) return "catalogs";
  if (path.startsWith("/reportes")) return "reports";
  if (path.startsWith("/auditoria")) return "audit";
  if (path.startsWith("/plantillas")) return "templates";
  if (path.startsWith("/api-keys")) return "apiKeys";
  if (path.startsWith("/usuarios")) return "users";
  if (path.startsWith("/empresas")) return "tenants";
  if (path.startsWith("/marca-app")) return "branding";
  if (path.startsWith("/planes-facturacion") ||
    path.startsWith("/tipos-impuesto") ||
    path.startsWith("/suscripciones") ||
    path.startsWith("/exportacion-erp") ||
    path.startsWith("/conversion-erp") ||
    path.startsWith("/ajustes-numeracion") ||
    path.startsWith("/plantilla-factura")) return "billing";
  if (path.startsWith("/financiero")) return "financial";
  if (path.startsWith("/backup")) return "backup";
  if (path.startsWith("/almacenes")) return "storage";
  if (path.startsWith("/huerfanos-s3")) return "storage";
  return "settings";
}
